import React from 'react';
import GlassCard from './ui/GlassCard';
import AnimatedCounter from './ui/AnimatedCounter';
import AnimatedTitle from './ui/AnimatedTitle';
import LazyLoad from './ui/LazyLoad';
import StaggeredAnimation from './ui/StaggeredAnimation';

interface MyAccountProps {
  totalAssets?: number;
  totalProfit?: number;
  todayProfit?: number;
  profitRate?: number;
  loading?: boolean;
}

const MyAccount: React.FC<MyAccountProps> = ({
  totalAssets = 0,
  totalProfit = 0,
  todayProfit = 0,
  profitRate = 0,
  loading = false,
}) => {
  const accountItems = [
    {
      title: '总资产',
      value: totalAssets,
      prefix: '$',
      suffix: '',
      decimals: 2,
      icon: '💰',
      color: 'text-blue-400',
      bgColor: 'bg-blue-500/20',
    },
    {
      title: '总收益',
      value: Math.abs(totalProfit),
      prefix: totalProfit >= 0 ? '+$' : '-$',
      suffix: '',
      decimals: 2,
      icon: '📈',
      color: totalProfit >= 0 ? 'text-green-400' : 'text-red-400',
      bgColor: totalProfit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20',
    },
    {
      title: '今日收益',
      value: Math.abs(todayProfit),
      prefix: todayProfit >= 0 ? '+$' : '-$',
      suffix: '',
      decimals: 2,
      icon: '📊',
      color: todayProfit >= 0 ? 'text-green-400' : 'text-red-400',
      bgColor: todayProfit >= 0 ? 'bg-green-500/20' : 'bg-red-500/20',
    },
    {
      title: '收益率',
      value: Math.abs(profitRate),
      prefix: profitRate >= 0 ? '+' : '-',
      suffix: '%',
      decimals: 2,
      icon: '🎯',
      color: profitRate >= 0 ? 'text-green-400' : 'text-red-400',
      bgColor: profitRate >= 0 ? 'bg-green-500/20' : 'bg-red-500/20',
    },
  ];

  if (loading) {
    return (
      <LazyLoad>
        <GlassCard className="p-6">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white"></div>
          </div>
        </GlassCard>
      </LazyLoad>
    );
  }

  return (
    <LazyLoad>
      <GlassCard className="p-6">
        <StaggeredAnimation delay={200}>
          {[
            <AnimatedTitle key="header" variant="gradient" size="xl" className="mb-6">
              我的账户
            </AnimatedTitle>,
          ]}
        </StaggeredAnimation>

        <StaggeredAnimation delay={400}>
          {[
            <div key="grid" className="grid grid-cols-2 gap-4">
              {accountItems.map((item, index) => (
                <StaggeredAnimation key={item.title} delay={600 + index * 100}>
                  {[
                    <div
                      key={item.title}
                      className={`${item.bgColor} rounded-xl p-4 border border-white/10 hover:border-white/20 transition-all duration-300 hover:scale-105`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-2xl">{item.icon}</span>
                        <span className="text-sm text-gray-300">{item.title}</span>
                      </div>
                      <div className={`text-xl font-bold ${item.color}`}>
                        <AnimatedCounter
                          value={item.value}
                          prefix={item.prefix}
                          suffix={item.suffix}
                          decimals={item.decimals}
                          duration={1000}
                        />
                      </div>
                    </div>,
                  ]}
                </StaggeredAnimation>
              ))}
            </div>,
          ]}
        </StaggeredAnimation>

        <StaggeredAnimation delay={1000}>
          {[
            <div
              key="overview"
              className="mt-6 p-4 bg-gradient-to-r from-blue-500/10 to-purple-500/10 rounded-xl border border-white/10"
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <span className="text-lg">📊</span>
                  <span className="text-white font-medium">账户概览</span>
                </div>
                <div className="text-sm text-gray-300">实时更新</div>
              </div>
              <div className="mt-2 text-sm text-gray-400">您的投资组合表现和收益情况</div>
            </div>,
          ]}
        </StaggeredAnimation>
      </GlassCard>
    </LazyLoad>
  );
};

export default MyAccount;
